/**
 * Where one figure came from, in the agreed value state's own terms.
 *
 * Every figure answers a different question depending on its state, so this
 * shows the evidence each state promises — never "Not recorded":
 *
 *   Stated     the document, the page / cell / line, the date it said so, and
 *              whose statement it is (the sponsor's model, the broker, …)
 *   Derived    the formula, each input value, the engine build and the time
 *   Suggested  the basis of the assumption, and that it is not data yet
 *   Confirmed  who, when and why
 *
 * One component for every surface that explains a figure — the Audit row,
 * the metric-bar dialog — so they cannot disagree about the same value.
 */
import type { ReactNode } from 'react'

import type { LoanFieldLineageField } from '@/pages/pipeline-intelligence/types'
import { FieldStateBadge, type FieldState } from './fieldState'
import { attributionText, lineageSummary, stampText, stateOfLineage } from './lineage'
import { formatVarianceFigure } from '@/components/deal/dossier/format'

function display(value: unknown): string {
  if (value === null || value === undefined || value === '') return '—'
  if (typeof value === 'number') {
    return Number.isInteger(value)
      ? value.toLocaleString()
      : value.toLocaleString(undefined, { maximumFractionDigits: 4 })
  }
  if (typeof value === 'object') return JSON.stringify(value)
  return String(value)
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-3 text-[11px]">
      <span className="shrink-0 text-stone-500">{label}</span>
      <span className="min-w-0 break-words text-right font-mono text-stone-200">{children}</span>
    </div>
  )
}

export function ValueProvenance({
  field,
  state: stateOverride,
  onOpenSource,
  onConfirm,
  confirming = false,
  kind,
}: {
  field: LoanFieldLineageField | null | undefined
  /** The field's catalogue kind. Given, a variance prints each figure with
   *  its unit on the scale the server states it in (a percent is points). */
  kind?: string
  /** Force a state — the dossier's own unsaved recalculation is Derived. */
  state?: FieldState
  /** Open the document a stated value came from. */
  onOpenSource?: (document: string | null) => void
  /** Offered on a Stated, Derived or Suggested figure: a person confirming it. */
  onConfirm?: () => void
  confirming?: boolean
}) {
  const state = stateOverride ?? stateOfLineage(field)
  const prov = field?.provenance ?? null
  const derivation = field?.derivation ?? null
  const suggestion = field?.suggestion ?? null
  const confirmation = field?.confirmation ?? null
  const variance = field?.variance ?? null

  return (
    <div className="space-y-2" data-testid="value-provenance">
      <div className="flex items-center gap-2">
        <FieldStateBadge state={state} />
        <span className="min-w-0 truncate text-[11px] text-stone-400">{lineageSummary(field)}</span>
      </div>

      {state === 'stated' && prov && (
        <div className="space-y-1">
          <Row label="Document">
            {onOpenSource && prov.document ? (
              <button
                type="button"
                onClick={() => onOpenSource(prov.document ?? null)}
                className="text-sky-300 underline decoration-sky-400/40 underline-offset-2 hover:text-sky-200"
              >
                {prov.document}
              </button>
            ) : (
              prov.document ?? '—'
            )}
          </Row>
          {prov.page ? <Row label="Page">{prov.page}</Row> : null}
          {prov.cellRef ? <Row label="Cell">{prov.cellRef}</Row> : null}
          {prov.lineNo != null && prov.lineNo !== '' ? <Row label="Line">{String(prov.lineNo)}</Row> : null}
          {prov.statedAt ? <Row label="Stated on">{stampText(prov.statedAt)}</Row> : null}
          {prov.attribution ? (
            <Row label="Whose statement">{attributionText(prov.attribution)}</Row>
          ) : null}
          {prov.inheritedFrom ? (
            <Row label="Read as part of">{prov.inheritedFrom.replace(/_/g, ' ')}</Row>
          ) : null}
          {prov.raw ? <Row label="Text">{prov.raw}</Row> : null}
        </div>
      )}

      {state === 'derived' && derivation && (
        <div className="space-y-1">
          <Row label="Formula">{derivation.formula ?? '—'}</Row>
          {derivation.inputs.map((input) => (
            <Row key={input.field} label={input.label}>
              {display(input.value)}
              {input.source ? <span className="ml-1.5 text-[10px] text-stone-500">· {input.source}</span> : null}
            </Row>
          ))}
          {!derivation.inputsRecorded && derivation.inputs.length > 0 && (
            <p className="text-[10px] text-stone-500">
              Computed before input values were recorded — the inputs shown are their values now.
            </p>
          )}
          <Row label="Engine">{derivation.engineVersion ?? 'build not recorded at the time'}</Row>
          {derivation.computedAt ? <Row label="Computed">{stampText(derivation.computedAt)}</Row> : null}
          {derivation.note ? <p className="text-[10px] text-stone-500">{derivation.note}</p> : null}
        </div>
      )}

      {state === 'suggested' && suggestion && (
        <div className="space-y-1">
          <Row label="Basis">{suggestion.basis}</Row>
          <p className="text-[10px] text-stone-500">
            A suggestion is not data: it is not counted until a person confirms it.
          </p>
        </div>
      )}

      {state === 'confirmed' && confirmation && (
        <div className="space-y-1">
          <Row label="Confirmed by">{confirmation.by ?? '—'}</Row>
          {confirmation.at ? <Row label="When">{stampText(confirmation.at)}</Row> : null}
          {confirmation.rationale ? (
            <Row label="Why">{confirmation.rationale}</Row>
          ) : (
            <p className="text-[10px] text-stone-500">No rationale recorded</p>
          )}
          {prov?.document ? <Row label="Against">{prov.document}</Row> : null}
        </div>
      )}

      {field && (field.confidence != null || field.editorEmail || field.updatedAt) ? (
        <div className="space-y-1 border-t border-white/[0.04] pt-1.5">
          {field.confidence != null ? (
            <Row label="Confidence">{`${Math.round(field.confidence * 100)}%`}</Row>
          ) : null}
          {field.editorEmail ? <Row label="Last written by">{field.editorEmail}</Row> : null}
          {field.updatedAt ? <Row label="Last written">{stampText(field.updatedAt)}</Row> : null}
        </div>
      ) : null}

      {variance && (
        <div className="rounded border border-amber-400/20 bg-amber-400/[0.04] px-2 py-1.5 text-[10px] text-amber-100/90">
          Variance, for information: stated {kind ? formatVarianceFigure(variance.stated, kind) : display(variance.stated)}; the deal&rsquo;s own
          figures give {kind ? formatVarianceFigure(variance.computed, kind) : display(variance.computed)}
          {variance.formula ? ` (${variance.formula})` : ''}. Nothing is chosen between them — if an
          input is wrong, correct the input.
        </div>
      )}

      {onConfirm && state !== 'confirmed' && state !== 'missing' && state !== 'not_applicable' && (
        <button
          type="button"
          onClick={onConfirm}
          disabled={confirming}
          className="rounded border border-emerald-500/30 px-2 py-1 text-[10px] text-emerald-300 hover:bg-emerald-500/10 disabled:opacity-40"
        >
          {confirming ? 'Confirming…' : 'Confirm this value'}
        </button>
      )}
    </div>
  )
}
